import { useRef, useCallback } from 'react'
import { Trash2, History } from 'lucide-react'
import { useCardHistoryStore } from '@/stores/cardHistoryStore'
import { useSidePanelStore } from '@/stores/sidePanelStore'
import { api } from '@/services/api'
import type { Card } from '@/types'
import HistoryEntry from './HistoryEntry'

interface HistoryPanelProps {
  expanded?: boolean
}

export default function HistoryPanel({ expanded = false }: HistoryPanelProps) {
  const { entries, clearHistory } = useCardHistoryStore()
  const { openCardModal, setHoverPreview, scheduleDismissPreview, cancelDismissPreview, hoverPreviewCard } = useSidePanelStore()
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const cardCacheRef = useRef<Map<string, Card>>(new Map())

  const fetchCard = useCallback(async (cardId: string): Promise<Card | null> => {
    const cached = cardCacheRef.current.get(cardId)
    if (cached) return cached
    try {
      const { data: card } = await api.get(`/api/cards/${cardId}`)
      cardCacheRef.current.set(cardId, card)
      return card
    } catch {
      return null
    }
  }, [])

  const handleEntryClick = async (entry: (typeof entries)[0]) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHoverPreview(null)
    const card = await fetchCard(entry.cardId)
    if (card) openCardModal(card)
  }

  const handleHoverStart = (entry: (typeof entries)[0]) => {
    // Cancel any pending dismiss
    cancelDismissPreview()

    // If already previewing this card, do nothing
    if (hoverPreviewCard?.id === entry.cardId) return

    // Cancel any pending hover trigger for a different card
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)

    hoverTimerRef.current = setTimeout(async () => {
      const card = await fetchCard(entry.cardId)
      if (card) setHoverPreview(card)
    }, 500)
  }

  const handleHoverEnd = () => {
    // Cancel any pending hover trigger
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    // Schedule dismiss with grace period
    scheduleDismissPreview()
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-dark-muted px-6">
        <History className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No history yet</p>
        <p className="text-xs mt-1 text-center">
          Cards you view, add, or edit will appear here
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-1">
        {entries.map((entry) => (
          <HistoryEntry
            key={entry.id}
            entry={entry}
            expanded={expanded}
            onClick={() => handleEntryClick(entry)}
            onHoverStart={() => handleHoverStart(entry)}
            onHoverEnd={handleHoverEnd}
          />
        ))}
      </div>
      <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-dark-border">
        <button
          onClick={clearHistory}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 dark:text-dark-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Clear History
        </button>
      </div>
    </div>
  )
}
